import React, { useState } from "react";
import { Check, Copy, ExternalLink, Key, ArrowRight, Loader, RefreshCw } from "lucide-react";
import { api, getApiError } from "/src/api/client";

function detectProvider(repoUrl) {
  if (!repoUrl) return null;
  if (repoUrl.includes("github.com"))        return "github";
  if (repoUrl.includes("gitlab.com"))        return "gitlab";
  if (repoUrl.includes("bitbucket.org"))     return "bitbucket";
  if (repoUrl.includes("dev.azure.com") || repoUrl.includes("visualstudio.com")) return "azure";
  return "generic";
}

const PROVIDER_INSTRUCTIONS = {
  github: {
    name: "GitHub",
    steps: [
      "Go to your repository on GitHub.",
      'Open Settings → Deploy keys → "Add deploy key".',
      "Paste the public key below, give it a title (e.g. GoLive), and leave Allow write access unchecked.",
      'Click "Add key", then come back here and click "Start Analysis".',
    ],
    settingsPath: (repoUrl) => {
      const match = repoUrl.replace(/\.git$/, "").match(/github\.com[/:](.+)/);
      return match ? `https://github.com/${match[1]}/settings/keys/new` : null;
    },
  },
  gitlab: {
    name: "GitLab",
    steps: [
      "Go to your project on GitLab.",
      'Open Settings → Repository → Deploy keys → "Add new key".',
      "Paste the public key, give it a title, and leave Write access unchecked.",
      'Click "Add key", then come back here and click "Start Analysis".',
    ],
    settingsPath: (repoUrl) => {
      const match = repoUrl.replace(/\.git$/, "").match(/gitlab\.com[/:](.+)/);
      return match ? `https://gitlab.com/${match[1]}/-/settings/repository` : null;
    },
  },
  bitbucket: {
    name: "Bitbucket",
    steps: [
      "Go to your repository on Bitbucket.",
      'Open Repository settings → Access keys → "Add key".',
      "Paste the public key and give it a label (e.g. GoLive).",
      'Click "Add key", then come back here and click "Start Analysis".',
    ],
    settingsPath: (repoUrl) => {
      const match = repoUrl.replace(/\.git$/, "").match(/bitbucket\.org[/:](.+)/);
      return match ? `https://bitbucket.org/${match[1]}/admin/access-keys/` : null;
    },
  },
  azure: {
    name: "Azure DevOps",
    steps: [
      "Go to your repository in Azure DevOps.",
      'Open Project Settings → Repositories → select your repo → Security → SSH Keys → "Add".',
      "Paste the public key and give it a description.",
      'Click "Add", then come back here and click "Start Analysis".',
    ],
    settingsPath: () => null,
  },
  generic: {
    name: "your Git provider",
    steps: [
      "Open your repository's settings and find the Deploy Keys or SSH Keys section.",
      "Add a new key with read-only access.",
      "Paste the public key below.",
      'Save, then come back here and click "Start Analysis".',
    ],
    settingsPath: () => null,
  },
};

export default function DeployKeySetup({ run, onStarted }) {
  const [copied, setCopied]           = useState(false);
  const [starting, setStarting]       = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerated, setRegenerated] = useState(false);
  const [publicKey, setPublicKey]     = useState(run.ssh_public_key);
  const [error, setError]             = useState(null);

  const providerKey  = detectProvider(run.repo_url) || "generic";
  const provider     = PROVIDER_INSTRUCTIONS[providerKey];
  const settingsUrl  = provider.settingsPath(run.repo_url);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard not available */
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    setError(null);
    setRegenerated(false);
    try {
      const { data } = await api.post(`/golive/deployment_requests/${run.id}/regenerate-key`);
      setPublicKey(data.ssh_public_key);
      setRegenerated(true);
      setTimeout(() => setRegenerated(false), 3000);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setRegenerating(false);
    }
  };

  const handleStart = async () => {
    setStarting(true);
    setError(null);
    try {
      await api.post(`/golive/deployment_requests/${run.id}/start`);
      onStarted();
    } catch (err) {
      setError(getApiError(err));
      setStarting(false);
    }
  };

  return (
    <div className="bg-white border border-amber-200 rounded-xl p-6 flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
          <Key size={18} className="text-amber-600" />
        </div>
        <div>
          <p className="font-semibold text-slate-800">One-time setup required</p>
          <p className="text-sm text-slate-500 mt-0.5">
            This is a private repository. Add the deploy key below to{" "}
            <span className="font-medium">{provider.name}</span> so GoLive can clone it securely.
            The key is read-only and scoped to this repository only.
          </p>
        </div>
      </div>

      {/* Previous attempt error */}
      {run.status === "failed" && run.error_message && (
        <div className="flex flex-col gap-1.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
          <p className="text-xs font-semibold text-red-700 uppercase tracking-wider">Previous attempt failed</p>
          <pre className="text-xs text-red-700 font-mono whitespace-pre-wrap break-all">
            {run.error_message
              .replace(/git clone failed:\s*/i, "")
              .replace(/Cloning into '[^']*'\.\.\.\s*/gi, "")
              .trim()
            }
          </pre>
        </div>
      )}

      {/* Steps */}
      <ol className="flex flex-col gap-2">
        {provider.steps.map((step, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
            <span className="shrink-0 w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">
              {i + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>

      {/* Public key box */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Public key</p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleRegenerate}
              disabled={regenerating}
              title="Generate a new key pair — you'll need to re-add the new public key on your provider"
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 disabled:opacity-50 transition-colors"
            >
              {regenerating
                ? <Loader size={13} className="animate-spin" />
                : regenerated
                  ? <Check size={13} className="text-green-500" />
                  : <RefreshCw size={13} />
              }
              {regenerating ? "Regenerating…" : regenerated ? "New key ready" : "Regenerate"}
            </button>
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
            >
              {copied ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>
        <pre className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 font-mono break-all whitespace-pre-wrap select-all">
          {publicKey}
        </pre>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        {settingsUrl && (
          <a
            href={settingsUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 transition-colors"
          >
            <ExternalLink size={13} />
            Open {provider.name} deploy key settings
          </a>
        )}
        <button
          type="button"
          onClick={handleStart}
          disabled={starting}
          className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {starting
            ? <><Loader size={14} className="animate-spin" /> Starting…</>
            : <><span>I've added the deploy key — Start Analysis</span><ArrowRight size={14} /></>
          }
        </button>
      </div>
    </div>
  );
}
